"use client";

import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import "./Login.css";

// Type definitions
interface FormData {
  email: string;
  password: string;
}

interface FormErrors {
  email?: string;
  password?: string;
  general?: string;
}

interface LocationState {
  from?: {
    pathname: string;
  };
}

const Login: React.FC = () => {
  // Form state
  const [formData, setFormData] = useState<FormData>({
    email: "",
    password: "",
  });

  // UI state
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);

  // Hooks
  const { login, isAuthenticated } = useAuth();
  const { error, success } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  // Get the intended destination from location state
  const from =
    (location.state as LocationState)?.from?.pathname || "/dashboard";

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  // Handle input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Update form validity for submit button state
    const updatedFormData = { ...formData, [name]: value };
    setIsFormValid(
      updatedFormData.email.length > 0 && updatedFormData.password.length > 0
    );
  };

  // Handle form submission
  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    e.preventDefault();

    // Clear any previous errors
    setErrors({});

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await login({
        email: formData.email,
        password: formData.password,
      });

      success("Login successful!");
      // Navigation will be handled by the useEffect above
    } catch (err: any) {
      const errorMessage = err.message || "Login failed. Please try again.";
      // setErrors({ general: errorMessage });
      error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Required field validation
    if (!formData.email.trim()) newErrors.email = "Email is required";
    if (!formData.password) newErrors.password = "Password is required";

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Password validation
    if (formData.password && formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters long";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  return (
    <div className="login-page">
      {/* Header */}
      <div className="login-header">
        <Link to="/" className="login-logo-link">
          <div className="logo">
            <div className="logo-icon">
              <div className="login-logo-icon">⚡</div>
            </div>
            <span className="logo-text">Netzsync</span>
          </div>
        </Link>
      </div>

      {/* Main Content */}
      <div className="login-content">
        {/* Background Section */}
        <div className="login-background">
          {/* <img
            src="/images/login-background.png"
            alt="Data synchronization visualization"
            className="background-image"
          /> */}
        </div>

        {/* Login Panel */}
        <div className="login-panel">
          <div className="login-form-container">
            <h1 className="login-title">Login</h1>

            {errors.general && (
              <div className="error-message general-error">
                {errors.general}
              </div>
            )}

            <form onSubmit={handleSubmit} className="login-form" noValidate>
              <div className="form-group">
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="Email*"
                  value={formData.email}
                  onChange={handleChange}
                  className={`form-input ${errors.email ? "error" : ""}`}
                  required
                  autoComplete="email"
                  disabled={loading}
                />
                {errors.email && (
                  <span className="field-error">{errors.email}</span>
                )}
              </div>

              <div className="form-group">
                <input
                  type="password"
                  id="password"
                  name="password"
                  placeholder="Password*"
                  value={formData.password}
                  onChange={handleChange}
                  className={`form-input ${errors.password ? "error" : ""}`}
                  required
                  autoComplete="current-password"
                  disabled={loading}
                />
                {errors.password && (
                  <span className="field-error">{errors.password}</span>
                )}
              </div>

              <div className="forgot-password-section">
                <Link to="/forgot-password" className="forgot-password-link">
                  Forgot Password
                </Link>
              </div>

              <button
                type="submit"
                className={`login-button ${!isFormValid ? "disabled" : ""}`}
                disabled={loading || !isFormValid}
              >
                {loading ? <LoadingSpinner size="small" /> : "Login"}
              </button>

              <div className="divider">
                <span>✱</span>
                <div className="divider-line"></div>
                <span>✱</span>
                <div className="divider-line"></div>
                <span>✱</span>
              </div>

              <div className="register-section">
                <span>Don't have an account yet? </span>
                <Link to="/register" className="register-link">
                  Register Now
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
